import { shouldSkipObject } from '@/threejs/objectManager';
import * as THREE from 'three';
import type { Projector } from './projector';

type GradientOverlayUserData = {
  projectorOverlayIgnore?: boolean;
  projectorGradientOverlayState?: {
    transparent: boolean;
    depthWrite: boolean;
    depthTest: boolean;
    blending: THREE.Blending;
    premultipliedAlpha: boolean;
    blendSrc: number;
    blendDst: number;
    blendEquation: number;
  };
  projectorFrustumGradientOverlay?: {
    applied?: boolean;
    uniforms?: THREE.Shader['uniforms'];
  };
};

export type ProjectorFrustumGradientOverlayOptions = {
  maxProjectors?: number;
  gradientColor?: THREE.ColorRepresentation;
  gradientOpacity?: number;
  gradientEnabled?: boolean;
  depthSize?: number;
  depthBias?: number;
  facingThreshold?: number;
  useTextureArray?: boolean;
  luxMin?: number;
  luxMax?: number;
  luxReferenceDistance?: number;
  luxLogScale?: boolean;
  projectionTextureStrength?: number;
};

export class ProjectorFrustumGradientOverlay {
  private readonly maxProjectors: number;
  private readonly gradientColor: THREE.Color;
  private gradientOpacity: number;
  private readonly materials = new Set<THREE.Material>();
  private readonly matrices: THREE.Matrix4[];
  private readonly projectorPositions: THREE.Vector3[];
  private readonly projectorDirections: THREE.Vector3[];
  private readonly projectorLuxFactors: number[];
  private luxMin: number;
  private luxMax: number;
  private luxReferenceDistance: number;
  private luxLogScale: boolean;
  private computedLuxMax: number;
  private sceneLuxMin: number;
  private sceneLuxMax: number;
  private gradientEnabled: boolean;
  private depthTargets: THREE.WebGLRenderTarget[] = [];
  private depthTextures: THREE.DepthTexture[] = [];
  private readonly depthArrayTarget?: THREE.WebGLArrayRenderTarget;
  private readonly depthMaterial: THREE.MeshDepthMaterial;
  private readonly depthSize: number;
  private readonly depthBias: number;
  private readonly useTextureArray: boolean;
  private facingThreshold: number;
  private overlayOnly = false;
  private projectorTextures: (THREE.Texture | null)[];
  private projectionTextureStrength: number;

  constructor(options: ProjectorFrustumGradientOverlayOptions = {}) {
    this.maxProjectors = Math.max(1, options.maxProjectors ?? 16);
    this.gradientColor = new THREE.Color(options.gradientColor ?? 0x2aa6ff);
    this.gradientOpacity = options.gradientOpacity ?? 0.12;
    this.gradientEnabled = options.gradientEnabled ?? true;
    this.depthSize = options.depthSize ?? 1024;
    this.depthBias = options.depthBias ?? 0.0025;
    this.facingThreshold = options.facingThreshold ?? -1;
    this.useTextureArray = options.useTextureArray ?? false;
    this.luxMin = options.luxMin ?? 0;
    this.luxMax = options.luxMax ?? 0;
    this.luxReferenceDistance = options.luxReferenceDistance ?? 1.0;
    this.luxLogScale = options.luxLogScale ?? false;
    this.matrices = Array.from({ length: this.maxProjectors }, () => new THREE.Matrix4());
    this.projectorPositions = Array.from({ length: this.maxProjectors }, () => new THREE.Vector3());
    this.projectorDirections = Array.from({ length: this.maxProjectors }, () => new THREE.Vector3(0, 0, -1));
    this.projectorLuxFactors = Array.from({ length: this.maxProjectors }, () => 0);
    this.computedLuxMax = this.luxMax;
    this.sceneLuxMin = 0;
    this.sceneLuxMax = 0;
    this.projectorTextures = Array.from({ length: this.maxProjectors }, () => null);
    this.projectionTextureStrength = Math.max(0, Math.min(1, options.projectionTextureStrength ?? 1));
    this.depthMaterial = new THREE.MeshDepthMaterial();
    this.depthMaterial.blending = THREE.NoBlending;

    if (this.useTextureArray) {
      this.depthMaterial.depthPacking = THREE.RGBADepthPacking;
      const target = new THREE.WebGLArrayRenderTarget(this.depthSize, this.depthSize, this.maxProjectors, {
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
        depthBuffer: true,
        stencilBuffer: false,
      });
      target.texture.minFilter = THREE.NearestFilter;
      target.texture.magFilter = THREE.NearestFilter;
      target.texture.generateMipmaps = false;
      target.texture.wrapS = THREE.ClampToEdgeWrapping;
      target.texture.wrapT = THREE.ClampToEdgeWrapping;
      this.depthArrayTarget = target;
    } else {
      this.depthTargets = Array.from({ length: this.maxProjectors }, () => {
        const target = new THREE.WebGLRenderTarget(this.depthSize, this.depthSize);
        target.texture.minFilter = THREE.NearestFilter;
        target.texture.magFilter = THREE.NearestFilter;
        target.texture.generateMipmaps = false;
        target.depthTexture = new THREE.DepthTexture(this.depthSize, this.depthSize);
        target.depthTexture.format = THREE.DepthFormat;
        target.depthTexture.type = THREE.UnsignedIntType;
        target.depthTexture.minFilter = THREE.NearestFilter;
        target.depthTexture.magFilter = THREE.NearestFilter;
        return target;
      });
      this.depthTextures = this.depthTargets.map((target) => target.depthTexture);
    }
  }

  applyTo(root: THREE.Object3D) {
    root.traverse((object) => {
      if (!('isMesh' in object) || !(object as THREE.Mesh).isMesh) {
        return;
      }

      const mesh = object as THREE.Mesh;
      if ((mesh.userData as GradientOverlayUserData).projectorOverlayIgnore) {
        return;
      }

      if (Array.isArray(mesh.material)) {
        mesh.material.forEach((material) => this.decorateMaterial(material));
      } else if (mesh.material) {
        this.decorateMaterial(mesh.material);
      }
    });
  }

  update(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    projectors: Projector[],
    options: { overlayOnly?: boolean } = {}
  ) {
    const count = Math.min(projectors.length, this.maxProjectors);
    this.overlayOnly = options.overlayOnly ?? false;
    if (count === 0) {
      this.applyUniforms(0);
      return;
    }

    const previousRenderTarget = renderer.getRenderTarget();
    const previousClearColor = new THREE.Color();
    renderer.getClearColor(previousClearColor);
    const previousClearAlpha = renderer.getClearAlpha();
    const previousAutoClear = renderer.autoClear;
    const previousOverrideMaterial = scene.overrideMaterial;

    renderer.autoClear = true;
    scene.overrideMaterial = this.depthMaterial;
    renderer.setClearColor(0x000000, 1);

    const hiddenObjects: THREE.Object3D[] = [];
    scene.traverse((object: THREE.Object3D) => {
      if (object.visible && shouldSkipObject(object)) {
        object.visible = false;
        hiddenObjects.push(object);
      }
    });

    for (let i = 0; i < count; i += 1) {
      const projector = projectors[i];
      projector.updateMatrixWorld(true);
      projector.camera.updateMatrixWorld(true);
      this.matrices[i].multiplyMatrices(projector.camera.projectionMatrix, projector.camera.matrixWorldInverse);
      this.projectorPositions[i].setFromMatrixPosition(projector.camera.matrixWorld);
      projector.camera.getWorldDirection(this.projectorDirections[i]);
      this.projectorLuxFactors[i] = this.getLuxFactor(projector);
      this.projectorTextures[i] = (projector as any).userData?.texture ?? null;

      if (this.useTextureArray) {
        if (!this.depthArrayTarget) {
          continue;
        }
        renderer.setRenderTarget(this.depthArrayTarget, i);
      } else {
        renderer.setRenderTarget(this.depthTargets[i]);
      }
      renderer.clear();
      renderer.render(scene, projector.camera);
    }
    for (const object of hiddenObjects) {
      object.visible = true;
    }

    renderer.setRenderTarget(previousRenderTarget);
    renderer.setClearColor(previousClearColor, previousClearAlpha);
    renderer.autoClear = previousAutoClear;
    scene.overrideMaterial = previousOverrideMaterial;

    for (let i = count; i < this.maxProjectors; i += 1) {
      this.projectorTextures[i] = null;
    }

    this.applyUniforms(count);
  }

  setGradientOpacity(opacity: number) {
    this.gradientOpacity = Math.min(1, Math.max(0, opacity));
  }

  setGradientColor(color: THREE.ColorRepresentation) {
    this.gradientColor.set(color);
  }

  setProjectionTextureStrength(strength: number) {
    this.projectionTextureStrength = Math.max(0, Math.min(1, strength));
  }

  setFacingThreshold(threshold: number) {
    this.facingThreshold = Math.max(-1, Math.min(1, threshold));
  }

  clearOverlay() {
    this.overlayOnly = false;
    this.applyUniforms(0);
  }

  getBatchSize() {
    return this.maxProjectors;
  }

  beginOverlayPass() {
    for (const material of this.materials) {
      const userData = material.userData as GradientOverlayUserData;
      if (!userData.projectorGradientOverlayState) {
        userData.projectorGradientOverlayState = {
          transparent: material.transparent,
          depthWrite: material.depthWrite,
          depthTest: material.depthTest,
          blending: material.blending,
          premultipliedAlpha: material.premultipliedAlpha,
          blendSrc: material.blendSrc,
          blendDst: material.blendDst,
          blendEquation: material.blendEquation,
        };
      }

      material.transparent = false;
      material.depthWrite = true;
      material.depthTest = true;
      material.blending = THREE.NormalBlending;
      material.premultipliedAlpha = false;
    }
  }

  endOverlayPass() {
    for (const material of this.materials) {
      const userData = material.userData as GradientOverlayUserData;
      const state = userData.projectorGradientOverlayState;
      if (!state) {
        continue;
      }

      material.transparent = state.transparent;
      material.depthWrite = state.depthWrite;
      material.depthTest = state.depthTest;
      material.blending = state.blending;
      material.premultipliedAlpha = state.premultipliedAlpha;
      material.blendSrc = state.blendSrc;
      material.blendDst = state.blendDst;
      material.blendEquation = state.blendEquation;
    }
  }

  private decorateMaterial(material: THREE.Material) {
    if (!('isMeshStandardMaterial' in material) && !('isMeshPhysicalMaterial' in material)) {
      return;
    }

    const userData = material.userData as GradientOverlayUserData;
    if (userData.projectorFrustumGradientOverlay?.applied) {
      return;
    }

    const previous = material.onBeforeCompile;
    material.onBeforeCompile = (shader) => {
      previous?.(shader);

      shader.uniforms.uGradientProjectorCount = { value: 0 };
      shader.uniforms.uGradientProjectorMatrix = {
        value: this.matrices.map((m) => m.clone()),
      };
      shader.uniforms.uGradientProjectorColor = { value: this.gradientColor };
      shader.uniforms.uGradientProjectorOpacity = {
        value: this.gradientOpacity,
      };
      shader.uniforms.uGradientProjectorEnabled = {
        value: this.gradientEnabled ? 1 : 0,
      };
      shader.uniforms.uGradientProjectorPosition = {
        value: this.projectorPositions.map((p) => p.clone()),
      };
      shader.uniforms.uGradientProjectorDirection = {
        value: this.projectorDirections.map((d) => d.clone()),
      };
      shader.uniforms.uGradientProjectorLuxFactor = {
        value: this.projectorLuxFactors.slice(),
      };
      shader.uniforms.uGradientProjectorLuxMin = { value: this.luxMin };
      shader.uniforms.uGradientProjectorLuxMax = { value: this.computedLuxMax };
      shader.uniforms.uGradientProjectorLuxLog = {
        value: this.luxLogScale ? 1 : 0,
      };
      shader.uniforms.uGradientProjectorHasTexture = {
        value: this.projectorTextures.map((t) => (t ? 1 : 0)),
      };
      shader.uniforms.uGradientProjectorTextureStrength = {
        value: this.projectionTextureStrength,
      };
      for (let i = 0; i < this.maxProjectors; i += 1) {
        shader.uniforms[`uGradientProjectorTexture${i}`] = {
          value: this.projectorTextures[i] ?? null,
        };
      }
      shader.uniforms.uGradientProjectorOverlayOnly = { value: 0 };
      shader.uniforms.uGradientProjectorBias = { value: this.depthBias };
      shader.uniforms.uGradientProjectorFacingThreshold = {
        value: this.facingThreshold,
      };
      if (this.useTextureArray) {
        shader.uniforms.uGradientProjectorDepthArray = {
          value: this.depthArrayTarget?.texture ?? null,
        };
      } else {
        for (let i = 0; i < this.depthTextures.length; i += 1) {
          shader.uniforms[`uGradientProjectorDepth${i}`] = {
            value: this.depthTextures[i],
          };
        }
      }

      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\n' + 'varying vec3 vGradientProjectorWorldPosition;')
        .replace(
          '#include <project_vertex>',
          [
            '#include <project_vertex>',
            'vec4 gradientProjectorWorldPosition = vec4( transformed, 1.0 );',
            '#ifdef USE_BATCHING',
            '  gradientProjectorWorldPosition = batchingMatrix * gradientProjectorWorldPosition;',
            '#endif',
            '#ifdef USE_INSTANCING',
            '  gradientProjectorWorldPosition = instanceMatrix * gradientProjectorWorldPosition;',
            '#endif',
            'gradientProjectorWorldPosition = modelMatrix * gradientProjectorWorldPosition;',
            'vGradientProjectorWorldPosition = gradientProjectorWorldPosition.xyz;',
          ].join('\n')
        );

      const fragmentHeaderLines = [
        '#include <common>',
        `#define MAX_PROJECTORS ${this.maxProjectors}`,
        'varying vec3 vGradientProjectorWorldPosition;',
        'uniform int uGradientProjectorCount;',
        'uniform mat4 uGradientProjectorMatrix[MAX_PROJECTORS];',
        'uniform vec3 uGradientProjectorColor;',
        'uniform float uGradientProjectorOpacity;',
        'uniform float uGradientProjectorEnabled;',
        'uniform vec3 uGradientProjectorPosition[MAX_PROJECTORS];',
        'uniform vec3 uGradientProjectorDirection[MAX_PROJECTORS];',
        'uniform float uGradientProjectorLuxFactor[MAX_PROJECTORS];',
        'uniform float uGradientProjectorLuxMin;',
        'uniform float uGradientProjectorLuxMax;',
        'uniform float uGradientProjectorLuxLog;',
        'uniform float uGradientProjectorHasTexture[MAX_PROJECTORS];',
        'uniform float uGradientProjectorTextureStrength;',
        'uniform float uGradientProjectorOverlayOnly;',
        'uniform float uGradientProjectorBias;',
        'uniform float uGradientProjectorFacingThreshold;',
      ];

      if (this.useTextureArray) {
        fragmentHeaderLines.push('uniform sampler2DArray uGradientProjectorDepthArray;');
      } else {
        fragmentHeaderLines.push(
          ...Array.from({ length: this.maxProjectors }, (_, i) => `uniform sampler2D uGradientProjectorDepth${i};`)
        );
      }

      if (this.useTextureArray) {
        fragmentHeaderLines.push(
          'float gradientOverlayUnpackRGBAToDepth(const in vec4 v) {',
          '  const vec4 UnpackFactors4 = vec4(',
          '    255.0 / 256.0,',
          '    (255.0 / 256.0) / 256.0,',
          '    (255.0 / 256.0) / (256.0 * 256.0),',
          '    1.0 / (256.0 * 256.0 * 256.0)',
          '  );',
          '  return dot(v, UnpackFactors4);',
          '}'
        );
      }

      // Per-projector texture samplers
      fragmentHeaderLines.push(
        ...Array.from({ length: this.maxProjectors }, (_, i) => `uniform sampler2D uGradientProjectorTexture${i};`)
      );

      if (this.useTextureArray) {
        // Helper function to sample projector texture by index (needed for loop path)
        fragmentHeaderLines.push(
          'vec4 sampleGradientProjectorTexture(int idx, vec2 uv) {',
          ...Array.from(
            { length: this.maxProjectors },
            (_, i) => `  if (idx == ${i}) return texture(uGradientProjectorTexture${i}, uv);`
          ),
          '  return vec4(0.0);',
          '}'
        );
      }

      const fragmentHeader = fragmentHeaderLines.join('\n');

      let overlayBlocks = '';
      if (this.useTextureArray) {
        overlayBlocks = [
          'float gradientProjectorFillCount = 0.0;',
          'float gradientProjectorLux = 0.0;',
          'vec4 gradientProjectorTexture = vec4(0.0);',
          'float gradientProjectorTexCount = 0.0;',
          'vec3 gradientViewPos = -vViewPosition;',
          'for (int i = 0; i < MAX_PROJECTORS; i++) {',
          '  if (i >= uGradientProjectorCount) { break; }',
          '  vec4 clip = uGradientProjectorMatrix[i] * vec4(vGradientProjectorWorldPosition, 1.0);',
          '  if (clip.w <= 0.0) { continue; }',
          '  vec3 ndc = clip.xyz / clip.w;',
          '  if (ndc.x < -1.0 || ndc.x > 1.0 || ndc.y < -1.0 || ndc.y > 1.0 || ndc.z < -1.0 || ndc.z > 1.0) { continue; }',
          '  vec2 uv = ndc.xy * 0.5 + 0.5;',
          '  float depthSample = gradientOverlayUnpackRGBAToDepth(texture(uGradientProjectorDepthArray, vec3(uv, float(i))));',
          '  float fragDepth = ndc.z * 0.5 + 0.5;',
          '  if (fragDepth - uGradientProjectorBias > depthSample) { continue; }',
          '  if (uGradientProjectorFacingThreshold > -0.999) {',
          '    vec3 projectorViewPos = (viewMatrix * vec4(uGradientProjectorPosition[i], 1.0)).xyz;',
          '    vec3 projectorDir = normalize(projectorViewPos - gradientViewPos);',
          '    if (dot(normal, projectorDir) <= uGradientProjectorFacingThreshold) { continue; }',
          '  }',
          '  gradientProjectorFillCount += 1.0;',
          '  vec3 projectorToFragment = vGradientProjectorWorldPosition - uGradientProjectorPosition[i];',
          '  float depthToProjector = max(dot(projectorToFragment, uGradientProjectorDirection[i]), 1e-4);',
          '  float lux = uGradientProjectorLuxFactor[i] / max(depthToProjector * depthToProjector, 1e-4);',
          '  gradientProjectorLux += lux;',
          '  if (uGradientProjectorHasTexture[i] > 0.5) {',
          '    gradientProjectorTexture += sampleGradientProjectorTexture(i, uv);',
          '    gradientProjectorTexCount += 1.0;',
          '  }',
          '}',
          'float gradientProjectorFill = step(0.5, gradientProjectorFillCount);',
          'float gradientFillAlpha = gradientProjectorFill * uGradientProjectorOpacity;',
          'float gradientLuxT;',
          'if (uGradientProjectorLuxLog > 0.5) {',
          '  float minLux = max(uGradientProjectorLuxMin, 1e-4);',
          '  float maxLux = max(uGradientProjectorLuxMax, minLux + 1e-4);',
          '  float luxValue = max(gradientProjectorLux, minLux);',
          '  gradientLuxT = (log(luxValue) - log(minLux)) / max(log(maxLux) - log(minLux), 1e-4);',
          '} else {',
          '  float luxRange = max(uGradientProjectorLuxMax - uGradientProjectorLuxMin, 1e-4);',
          '  gradientLuxT = (gradientProjectorLux - uGradientProjectorLuxMin) / luxRange;',
          '}',
          'gradientLuxT = clamp(gradientLuxT, 0.0, 1.0);',
          'float gradientT = mix(1.0, gradientLuxT, uGradientProjectorEnabled);',
          'vec3 gradientFillColor = mix(vec3(0.0), uGradientProjectorColor, gradientT);',
          'if (gradientProjectorTexCount > 0.0) {',
          '  float texCount = max(gradientProjectorTexCount, 1.0);',
          '  vec4 texSample = gradientProjectorTexture / texCount;',
          '  // Texture color IS the projected light. Black (0,0,0) adds no light, letting surface show through',
          '  vec3 projectedLight = texSample.rgb * gradientT;',
          '  gradientFillColor = mix(gradientFillColor, projectedLight, uGradientProjectorTextureStrength);',
          '  // Reduce alpha for darker texture colors, but less aggressively for high-power projectors',
          '  float textureBrightness = dot(texSample.rgb, vec3(0.299, 0.587, 0.114));',
          '  // Remap brightness: black=0.15, white=1.0 for more realistic high-lumen projection',
          '  float alphaMod = mix(0.15, 1.0, textureBrightness) * texSample.a;',
          '  gradientFillAlpha *= mix(1.0, alphaMod, uGradientProjectorTextureStrength);',
          '}',
          'if (uGradientProjectorOverlayOnly > 0.5) {',
          '  // Overlay pass mode: add gradient to existing material color',
          '  outgoingLight = outgoingLight + gradientFillColor * gradientFillAlpha;',
          '} else {',
          '  // Projected light illuminates the surface material color',
          '  vec3 projectorIllumination = gradientFillColor * diffuseColor.rgb;',
          '  outgoingLight = outgoingLight + projectorIllumination * gradientFillAlpha;',

          '}',
        ].join('\n');
      } else {
        overlayBlocks = [
          'float gradientProjectorFillCount = 0.0;',
          'float gradientProjectorLux = 0.0;',
          'vec4 gradientProjectorTexture = vec4(0.0);',
          'float gradientProjectorTexCount = 0.0;',
          'vec3 gradientViewPos = -vViewPosition;',
          ...Array.from({ length: this.maxProjectors }, (_, i) => {
            return [
              `if (uGradientProjectorCount > ${i}) {`,
              `  vec4 clip${i} = uGradientProjectorMatrix[${i}] * vec4(vGradientProjectorWorldPosition, 1.0);`,
              `  if (clip${i}.w > 0.0) {`,
              `    vec3 ndc${i} = clip${i}.xyz / clip${i}.w;`,
              `    if (ndc${i}.x >= -1.0 && ndc${i}.x <= 1.0 && ndc${i}.y >= -1.0 && ndc${i}.y <= 1.0 && ndc${i}.z >= -1.0 && ndc${i}.z <= 1.0) {`,
              `      vec2 uv${i} = ndc${i}.xy * 0.5 + 0.5;`,
              `      float depthSample${i} = texture2D(uGradientProjectorDepth${i}, uv${i}).x;`,
              `      float fragDepth${i} = ndc${i}.z * 0.5 + 0.5;`,
              `      if (fragDepth${i} - uGradientProjectorBias <= depthSample${i}) {`,
              `        float facing${i} = dot(normal, normalize((viewMatrix * vec4(uGradientProjectorPosition[${i}], 1.0)).xyz - gradientViewPos));`,
              `        if (uGradientProjectorFacingThreshold <= -0.999 || facing${i} > uGradientProjectorFacingThreshold) {`,
              `          gradientProjectorFillCount += 1.0;`,
              `          vec3 projectorToFragment${i} = vGradientProjectorWorldPosition - uGradientProjectorPosition[${i}];`,
              `          float depthToProjector${i} = max(dot(projectorToFragment${i}, uGradientProjectorDirection[${i}]), 1e-4);`,
              `          float lux${i} = uGradientProjectorLuxFactor[${i}] / max(depthToProjector${i} * depthToProjector${i}, 1e-4);`,
              `          gradientProjectorLux += lux${i};`,
              `          if (uGradientProjectorHasTexture[${i}] > 0.5) {`,
              `            gradientProjectorTexture += texture2D(uGradientProjectorTexture${i}, uv${i});`,
              `            gradientProjectorTexCount += 1.0;`,
              `          }`,
              '        }',
              '      }',
              '    }',
              '  }',
              '}',
            ].join('\n');
          }),
          'float gradientProjectorFill = step(0.5, gradientProjectorFillCount);',
          'float gradientFillAlpha = gradientProjectorFill * uGradientProjectorOpacity;',
          'float gradientLuxT;',
          'if (uGradientProjectorLuxLog > 0.5) {',
          '  float minLux = max(uGradientProjectorLuxMin, 1e-4);',
          '  float maxLux = max(uGradientProjectorLuxMax, minLux + 1e-4);',
          '  float luxValue = max(gradientProjectorLux, minLux);',
          '  gradientLuxT = (log(luxValue) - log(minLux)) / max(log(maxLux) - log(minLux), 1e-4);',
          '} else {',
          '  float luxRange = max(uGradientProjectorLuxMax - uGradientProjectorLuxMin, 1e-4);',
          '  gradientLuxT = (gradientProjectorLux - uGradientProjectorLuxMin) / luxRange;',
          '}',
          'gradientLuxT = clamp(gradientLuxT, 0.0, 1.0);',
          'float gradientT = mix(1.0, gradientLuxT, uGradientProjectorEnabled);',
          'vec3 gradientFillColor = mix(vec3(0.0), uGradientProjectorColor, gradientT);',
          'if (gradientProjectorTexCount > 0.0) {',
          '  float texCount = max(gradientProjectorTexCount, 1.0);',
          '  vec4 texSample = gradientProjectorTexture / texCount;',
          '  // Texture color IS the projected light. Black (0,0,0) adds no light, letting surface show through',
          '  vec3 projectedLight = texSample.rgb * gradientT;',
          '  gradientFillColor = mix(gradientFillColor, projectedLight, uGradientProjectorTextureStrength);',
          '  // Reduce alpha for darker texture colors, but less aggressively for high-power projectors',
          '  float textureBrightness = dot(texSample.rgb, vec3(0.299, 0.587, 0.114));',
          '  // Remap brightness: black=0.15, white=1.0 for more realistic high-lumen projection',
          '  float alphaMod = mix(0.15, 1.0, textureBrightness) * texSample.a;',
          '  gradientFillAlpha *= mix(1.0, alphaMod, uGradientProjectorTextureStrength);',
          '}',
          'if (uGradientProjectorOverlayOnly > 0.5) {',
          '  // Overlay pass mode: add gradient to existing material color',
          '  outgoingLight = outgoingLight + gradientFillColor * gradientFillAlpha;',
          '} else {',
          '  // Projected light illuminates the surface material color',
          '  vec3 projectorIllumination = gradientFillColor * diffuseColor.rgb;',
          '  outgoingLight = outgoingLight + projectorIllumination * gradientFillAlpha;',

          '}',
        ].join('\n');
      }

      const withHeader = shader.fragmentShader.replace('#include <common>', fragmentHeader);
      let fragment = withHeader.replace(
        '#include <output_fragment>',
        [overlayBlocks, '#include <output_fragment>'].join('\n')
      );

      if (fragment === withHeader) {
        fragment = withHeader.replace(
          '#include <opaque_fragment>',
          [overlayBlocks, '#include <opaque_fragment>'].join('\n')
        );
      }

      shader.fragmentShader = fragment;

      userData.projectorFrustumGradientOverlay = {
        applied: true,
        uniforms: shader.uniforms,
      };
    };

    material.needsUpdate = true;
    userData.projectorFrustumGradientOverlay = { applied: true };
    this.materials.add(material);
  }

  private applyUniforms(count: number) {
    for (const material of this.materials) {
      const data = (material.userData as GradientOverlayUserData).projectorFrustumGradientOverlay;
      if (!data?.uniforms) {
        continue;
      }

      data.uniforms.uGradientProjectorCount.value = count;
      data.uniforms.uGradientProjectorColor.value = this.gradientColor;
      data.uniforms.uGradientProjectorOpacity.value = this.gradientOpacity;
      data.uniforms.uGradientProjectorEnabled.value = this.gradientEnabled ? 1 : 0;
      data.uniforms.uGradientProjectorLuxMin.value = this.luxMin;
      data.uniforms.uGradientProjectorLuxMax.value = this.computedLuxMax;
      data.uniforms.uGradientProjectorLuxLog.value = this.luxLogScale ? 1 : 0;
      data.uniforms.uGradientProjectorOverlayOnly.value = this.overlayOnly ? 1 : 0;
      data.uniforms.uGradientProjectorBias.value = this.depthBias;
      data.uniforms.uGradientProjectorFacingThreshold.value = this.facingThreshold;
      data.uniforms.uGradientProjectorTextureStrength.value = this.projectionTextureStrength;
      const hasTextureTarget = data.uniforms.uGradientProjectorHasTexture.value as number[];
      for (let i = 0; i < count; i += 1) {
        const tex = this.projectorTextures[i];
        hasTextureTarget[i] = this.gradientEnabled && tex && this.projectionTextureStrength > 0 ? 1 : 0;
        if (data.uniforms[`uGradientProjectorTexture${i}`]) {
          data.uniforms[`uGradientProjectorTexture${i}`].value = tex ?? null;
        }
      }
      for (let i = count; i < this.maxProjectors; i += 1) {
        hasTextureTarget[i] = 0;
      }
      if (this.useTextureArray && data.uniforms.uGradientProjectorDepthArray) {
        data.uniforms.uGradientProjectorDepthArray.value = this.depthArrayTarget?.texture ?? null;
      }

      const target = data.uniforms.uGradientProjectorMatrix.value as THREE.Matrix4[];
      for (let i = 0; i < count; i += 1) {
        target[i].copy(this.matrices[i]);
      }
      const positionTarget = data.uniforms.uGradientProjectorPosition.value as THREE.Vector3[];
      const directionTarget = data.uniforms.uGradientProjectorDirection.value as THREE.Vector3[];
      const luxTarget = data.uniforms.uGradientProjectorLuxFactor.value as number[];
      for (let i = 0; i < count; i += 1) {
        positionTarget[i].copy(this.projectorPositions[i]);
        directionTarget[i].copy(this.projectorDirections[i]);
        luxTarget[i] = this.projectorLuxFactors[i];
      }
    }
  }

  setLuxLogScale(enabled: boolean) {
    this.luxLogScale = enabled;
  }

  setLuxReferenceDistance(distance: number) {
    this.luxReferenceDistance = Math.max(0.1, distance);
  }

  setLuxMax(maxLux: number) {
    this.luxMax = Math.max(0, maxLux);
  }

  setLuxMin(minLux: number) {
    this.luxMin = Math.max(0, minLux);
  }

  private getLuxFactor(projector: Projector) {
    const fovY = THREE.MathUtils.degToRad(projector.camera.fov);
    const tanY = Math.tan(fovY / 2);
    const tanX = tanY * projector.camera.aspect;
    const denominator = Math.max(4 * tanX * tanY, 1e-6);
    const lumens = Math.max(0, projector.userData?.lumens);
    const brightness = Math.max(0, projector.userData?.power_brightness / 100);
    const stacked = Math.max(1, (projector.userData as any)?.stackedProjectors ?? 1);
    const gain = Math.max(0, projector.userData?.gain ?? 0.5);
    return (lumens * brightness * stacked * gain) / denominator;
  }
}
